import React, { useMemo, useCallback } from 'react';

import find from 'lodash/find';
import isArray from 'lodash/isArray';

import { ID } from '../../../../types';

import {
  MultiSelectChangeCallbackType,
  MultiSelectDataType
} from '../../../../helpers/MultiSelect/types';

import {
  FETCH_NEW_FIN_STATUS_REASON_TAGS_QUERY,
  FetchNewFinStatusReasonTagsQueryResponse
} from '../../../newFinStatusReasonTags/query/fetchNewFinStatusReasonTags.query';

import { useFinPaginatedNewFinStatusReasonTags } from '../../../newFinStatusReasonTags/hooks/useFinPaginatedNewFinStatusReasonTags';

import { MultiSelect } from '../../../../helpers/MultiSelect';

import { NewFinStatusReasonTagCache } from '../../../newFinStatusReasonTags/NewFinStatusReasonTagCache';

interface TeamNewFinStatusReasonTagsMultiSelectProps {
  value: ID[];
  autoFocus?: boolean;
  onChange: (value: ID[]) => void;
  name?: string;
  menuPosition?: 'absolute' | 'fixed';
}

function TeamNewFinStatusReasonTagsMultiSelect({
  value,
  autoFocus,
  onChange,
  name,
  menuPosition
}: TeamNewFinStatusReasonTagsMultiSelectProps) {
  const {
    newFinStatusReasonTags,
    newFinStatusReasonTagsLoading,
    newFinStatusReasonTagsError
  } = useFinPaginatedNewFinStatusReasonTags<FetchNewFinStatusReasonTagsQueryResponse>(
    {
      query: FETCH_NEW_FIN_STATUS_REASON_TAGS_QUERY,
      cacheKey: NewFinStatusReasonTagCache.selectFieldCacheKey(),
      initialLimit: 1000
    }
  );

  const tagsData = useMemo<MultiSelectDataType[]>(() => {
    return newFinStatusReasonTags.map((tag) => ({
      value: tag.id,
      label: tag.name
    }));
  }, [newFinStatusReasonTags]);

  const handleChange = useCallback<MultiSelectChangeCallbackType>(
    (value: MultiSelectDataType) => {
      onChange(isArray(value) ? value.map((item) => item.value) : []);
    },
    [onChange]
  );

  const multiSelectValue = useMemo<MultiSelectDataType[]>(() => {
    return isArray(value)
      ? value.map((item) => find(tagsData, ['value', item]))
      : [];
  }, [value, tagsData]);

  return (
    <MultiSelect
      error={newFinStatusReasonTagsError}
      inputWrapperClassName="block"
      onChange={handleChange}
      value={multiSelectValue}
      multi={true}
      isSearchable={true}
      isLoading={newFinStatusReasonTagsLoading}
      optionsLoading={newFinStatusReasonTagsLoading}
      data={tagsData}
      classNamePrefix="av_multi_select"
      autoFocus={autoFocus}
      name={name}
      menuPosition={menuPosition}
    />
  );
}

export default TeamNewFinStatusReasonTagsMultiSelect;
